<template>
  <div
      class="status-traffic-light compact"
      :style="{ width: size + 'px' }"
  >
    <!-- LABEL (row 1) -->
    <div class="label">
      {{ label }}
    </div>

    <!-- LIGHT (row 2) -->
    <div
        class="light"
        :class="statusClass"
        :style="{ width: size + 'px', height: size + 'px' }"
        :title="label"
        aria-hidden="true"
    >
      <span class="dot"></span>

      <!-- subtle micro popper shards -->
      <span
          v-if="showConfetti"
          v-for="n in 4"
          :key="n"
          class="shard"
          :style="{ '--i': n }"
      />
    </div>

    <!-- VALUE (row 3) -->
    <div class="value">
      {{ value }}
    </div>
  </div>
</template>

<script>
// page-lifetime flag (resets on full reload)
let celebrationTriggered = false;

export default {
  name: 'StatusTrafficLight',

  props: {
    statusName: {
      type: String,
      required: true,
    },

    /**
     * Optional override for the bottom value.
     * If omitted, we derive something sensible from status.
     */
    value: {
      type: String,
      default: '',
    },

    size: {
      type: Number,
      default: 46,
    },
  },

  data() {
    return {
      showConfetti: false,
    };
  },

  computed: {
    normalisedStatus() {
      return (this.statusName || 'unknown')
          .toString()
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '_');
    },

    statusClass() {
      return `status-${this.normalisedStatus}`;
    },

    label() {
      return this.normalisedStatus.replace('_', ' ');
    },

    derivedValue() {
      // sensible defaults if no explicit value is provided
      switch (this.normalisedStatus) {
        case 'paid': return 'Done';
        case 'completed': return 'Done';
        case 'submitted': return 'Sent';
        case 'in_progress': return 'Active';
        case 'awaiting': return 'Waiting';
        default: return '';
      }
    },

    isPaid() {
      return this.normalisedStatus === 'paid';
    },
  },

  mounted() {
    if (!this.isPaid) return;

    if (!celebrationTriggered) {
      celebrationTriggered = true;
    }

    if (celebrationTriggered) {
      this.showConfetti = true;

      setTimeout(() => {
        this.showConfetti = false;
      }, 1200);
    }
  },
};
</script>

<style scoped lang="scss">
/* Match RadialMetric.compact exactly */
.status-traffic-light.compact {
  display: grid;
  justify-items: center;
  align-items: center;
  grid-template-rows: 30px 40px 30px;
}

/* LABEL (row 1) */
.label {
  font-size: 10px;
  font-weight: 600;
  color: #777;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

/* LIGHT (row 2) */
.light {
  position: relative;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #f4f4f4;
  overflow: visible;
}

/* DOT */
.dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  z-index: 2;
}

/* VALUE (row 3) – matches RadialMetric */
.value {
  font-size: 10px;
  font-weight: 700;
  color: #333;
  line-height: 1;
  white-space: nowrap;
}

/* STATUS COLOURS */
.status-awaiting .dot { background: #f0ad4e; }
.status-in_progress .dot { background: #4297d7; }
.status-submitted .dot { background: #6f42c1; }
.status-completing .dot { background: #9be3b1; }
.status-completed .dot { background: #36a266; }
.status-paid .dot { background: #1e7e34; }
.status-unknown .dot { background: #adb5bd; }

/* ===== MICRO PARTY POPPER ===== */

.shard {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 2px;
  height: 6px;
  border-radius: 1px;
  background: #9be3b1;
  opacity: 0;

  /* angles: -35deg .. +35deg */
  --angle: calc((var(--i) - 2.5) * 23deg);

  transform-origin: center;
  animation: pop-shard 0.9s ease-out forwards;
  animation-delay: calc(var(--i) * 0.04s);
}

.shard:nth-child(even) {
  background: #36a266;
}

@keyframes pop-shard {
  0% {
    transform:
        translate(-50%, -50%)
        rotate(var(--angle))
        translateY(0);
    opacity: 1;
  }

  70% {
    transform:
        translate(-50%, -50%)
        rotate(var(--angle))
        translateY(-22px);
    opacity: 0.8;
  }

  100% {
    transform:
        translate(-50%, -50%)
        rotate(var(--angle))
        translateY(-30px);
    opacity: 0;
  }
}
</style>
